import {useMemo} from 'react'
import {tryParseJSONObject} from '../utils/utils'
import {useOcapiDefaultSiteRaw} from './useOcapi'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {useShopperObjectsSite} from './useScapiCustomApi'

const addHostToImageURLs = (methodArray, host) => {
    if (!host) {
        return methodArray
    }

    return methodArray.map((method) => {
        if (method.image?.URL) {
            return {
                ...method,
                image: {
                    ...method.image,
                    URL: `https://${host}${method.image.URL}`
                }
            }
        }
        return method
    })
}

export default function useOcapiSite() {
    const siteQueryOptions = {
        // only execute once
        staleTime: Infinity,
        cacheTime: Infinity,
        retry: false,
        retryOnMount: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
        // force run on server as we need it to tune the helmet config
        forceOnServer: true
    }

    // use OCAPI only for the built-in site data
    const {data: defaultSite} = useOcapiDefaultSiteRaw({}, siteQueryOptions)
    // use SCAPI custom API for Stokke-specific site data
    const {data: customSiteData} = useShopperObjectsSite(siteQueryOptions)

    const ocapiSite = useMemo(() => {
        if (!defaultSite || !customSiteData) {
            return undefined
        }

        const customSite = customSiteData?.c_result ? customSiteData : {c_result: customSiteData}
        const customSiteResult = customSite?.c_result
        const host = defaultSite?.httpsHostname

        return {
            default: defaultSite,
            custom: {
                ...customSite,
                c_result: {
                    ...customSiteResult,
                    shippingMethods: customSiteResult?.shippingMethods
                        ? addHostToImageURLs(customSiteResult.shippingMethods, host)
                        : customSiteResult?.shippingMethods
                }
            }
        }
    }, [defaultSite, customSiteData])

    // expose the loaded ocapi site to the server context so helmet config can use it in ssr
    const {res} = useServerContext()
    if (res) {
        res.locals.ocapiSite = ocapiSite
    }

    const self = useMemo(() => {
        // create class with getters for the different ocapiSite properties
        return new (class {
            get isLoaded() {
                return !!ocapiSite
            }
            get allowedLocales() {
                return ocapiSite?.default?.allowedLocales
            }
            get arEnabled() {
                return ocapiSite?.custom?.c_result?.preferences?.arEnabled
            }
            get arModelList() {
                // TODO: cache the result
                const value = ocapiSite?.custom?.c_result?.preferences?.arModelList
                if (!value) return null
                try {
                    // The structure is [{description: String, pids: String[], file: String}, ...]
                    const modelList = JSON.parse(value)
                    // convert to {[pid]: {description: String, file: String}}
                    return Object.fromEntries(
                        modelList.flatMap(({pids, ...item}) => pids.map((pid) => [pid, item]))
                    )
                } catch (e) {
                    if (process.env.NODE_ENV !== 'production' && process.env.NODE_ENV !== 'test') {
                        console.warn('Error parsing arModelList', e)
                    }
                    return null
                }
            }
            get brightcoveEnabled() {
                return ocapiSite?.custom?.c_result?.preferences?.BrightcoveEnabled
            }
            get brightcoveNativeVideoEnabled() {
                return Boolean(
                    ocapiSite?.custom?.c_result?.preferences?.BrightcoveNativeVideoEnabled
                )
            }
            get contentOnlySiteShowPrices() {
                return ocapiSite?.custom?.c_result?.preferences?.contentOnlySiteShowPrices
            }
            get siteType() {
                return ocapiSite?.custom?.c_result?.preferences?.siteType
            }
            get cpsiaEnabled() {
                return ocapiSite?.custom?.c_result?.preferences?.cpsiaEnabled
            }
            get customPreferences() {
                return ocapiSite?.custom?.c_result?.preferences || {}
            }
            get sitePrefGeoLocation() {
                return tryParseJSONObject(
                    ocapiSite?.custom?.c_result?.preferences?.countryGeoCoordinates
                )
            }
            get d2bEnabled() {
                return ocapiSite?.custom?.c_result?.preferences?.d2bEnabled
            }
            get httpsDisBaseUrl() {
                return ocapiSite?.default?.httpsDisBaseUrl
            }
            get myStokkeConfig() {
                const myStokkeConfig = ocapiSite?.custom?.c_result?.myStokkeConfig || []

                let newConfig = [...myStokkeConfig]
                if (!myStokkeConfig.find((item) => item.id === 'dashboard')) {
                    newConfig.push({id: 'dashboard'})
                }

                return Object.fromEntries(
                    newConfig?.map((config, idx) => [config.id, {...config, order: idx}]) || []
                )
            }
            get npsEnabled() {
                return Boolean(ocapiSite?.custom?.c_result?.preferences?.npsEnabled)
            }
            get npsIframeUrl() {
                return ocapiSite?.custom?.c_result?.preferences?.npsIframeUrl
            }
            get parcelLabOrderStatusPageEnabled() {
                return ocapiSite?.custom?.c_result?.preferences?.parcelLabOrderStatusPageEnabled
            }
            get parcellabUserId() {
                return ocapiSite?.custom?.c_result?.preferences?.parcellabUserId || 1617747
            }
            get parcellabReturnOrderJS() {
                return ocapiSite?.custom?.c_result?.preferences?.parcellabReturnOrderJS
            }
            get parcellabReturnOrderCSS() {
                return ocapiSite?.custom?.c_result?.preferences?.parcellabReturnOrderCSS
            }
            get preferences() {
                return ocapiSite?.custom?.c_result?.preferences || {}
            }
            get showEngraving() {
                return ocapiSite?.custom?.c_result?.preferences?.showEngraving === true
            }
            get productFamilyImage() {
                return ocapiSite?.custom?.c_result?.productFamilyImage
            }
            get reCaptchaPublicKey() {
                return ocapiSite?.custom?.c_result?.preferences?.reCaptchaPublicKey
            }
            get japanPostClientID() {
                return ocapiSite?.custom?.c_result?.preferences?.japanPostClientID
            }
            get japanPostSecret() {
                return ocapiSite?.custom?.c_result?.preferences?.japanPostSecret
            }
            get shippingMethods() {
                return ocapiSite?.custom?.c_result?.shippingMethods
            }
            get taxationPolicy() {
                return ocapiSite?.custom?.c_result?.taxationPolicy
            }
            get shippingContentAssetId() {
                return ocapiSite?.custom?.c_result?.preferences?.shippingContentAssetId
            }
            get stokkeMasterCatalog() {
                return ocapiSite?.custom?.c_result?.httpsCatalogContentUrl?.stokkeMasterCatalog
            }
            get accountId() {
                return ocapiSite?.custom?.c_result?.preferences?.BrightcoveAccountID
            }
            get cartEnabledExpressPayments() {
                return ocapiSite?.custom?.c_result?.preferences?.cartEnabledExpressPayments
            }
            get checkoutEnabledExpressPayments() {
                return ocapiSite?.custom?.c_result?.preferences?.checkoutEnabledExpressPayments
            }
            get isExpressPaymentAddressValidationEnabled() {
                return ocapiSite?.custom?.c_result?.preferences
                    ?.isExpressPaymentAddressValidationEnabled
            }
            get assemblyVideoPlayerId() {
                return ocapiSite?.custom?.c_result?.preferences?.BrightcoveAssemblyVideoPlayerId
            }
            get pdpContentEnabled() {
                return ocapiSite?.custom?.c_result?.preferences?.pwaPdpContentEnabled
            }
            get engravingImage() {
                return ocapiSite?.custom?.c_result?.imageUrls?.engraving
            }
            get bvApiKey() {
                return ocapiSite?.custom?.c_result?.preferences?.bvApiKey
            }
            get redirectLinkPartnerAllProducts() {
                return ocapiSite?.custom?.c_result?.preferences?.redirectLinkPartnerAllProducts
            }
            get bvEnabled() {
                return ocapiSite?.custom?.c_result?.preferences?.bvEnabled
            }
            get httpsCatalogContentUrl() {
                return ocapiSite?.custom?.c_result?.httpsCatalogContentUrl
            }
            get logo() {
                return ocapiSite?.current?.custom?.c_result?.imageUrls?.logo
            }
            get errorPageImage() {
                return ocapiSite?.custom?.c_result?.imageUrls?.errorPageImage
            }
            get newsletterPreselected() {
                return ocapiSite?.custom?.c_result?.preferences?.newsletterPreselected
            }
            get sharedLibraryDis() {
                return ocapiSite?.custom?.c_result?.imageUrls?.sharedLibraryDis
            }
            get sharedLibraryStatic() {
                return ocapiSite?.custom?.c_result?.imageUrls?.sharedLibraryStatic
            }
            get catalogDis() {
                return ocapiSite?.custom?.c_result?.imageUrls?.catalogDis
            }
            get catalogStatic() {
                return ocapiSite?.custom?.c_result?.imageUrls?.catalogStatic
            }
        })()
    }, [ocapiSite])
    return self
}
